import { getAPIURL } from "./config";
import {
  getGeneralDesignAllContracts,
  getGeneralDesignUserReports,
} from "./paymentsProgressApi";

/**
 * Создать отчёт Эскизного проектирования
 * POST /sketch_design/reports/add/{project_id}
 * @param {number} projectId - ID проекта
 * @param {{ datetime: string, work_type_id: number, comment: string, seconds_spend: number, is_remote: boolean }} body
 * @returns {Promise<{ id: number, user_id: number, project_id: number, datetime: string, work_type_id: number, comment: string, seconds_spend: number, is_remote: boolean, user: object, project: object, work_type: object }>}
 */
export const addSketchDesignReport = async (projectId, body) => {
  const response = await fetch(
    `${getAPIURL()}sketch_design/reports/add/${projectId}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "include",
      body: JSON.stringify(body),
    }
  );
  if (!response.ok) {
    const errorText = await response.text();
    let message = `Ошибка создания отчёта ЭП: ${response.status} ${response.statusText}`;
    if (response.status === 422) {
      try {
        const err = JSON.parse(errorText);
        const details = Array.isArray(err.detail)
          ? err.detail.map((d) => d.msg || JSON.stringify(d)).join("; ")
          : err.detail;
        if (details) message = `Ошибка валидации: ${details}`;
      } catch {
        if (errorText) message += `. ${errorText}`;
      }
    } else if (errorText) {
      message += `. ${errorText}`;
    }
    throw new Error(message);
  }
  return response.json();
};

/**
 * Создать отчёт по проекту общего проектирования
 * POST /general_design/report
 * @param {{ datetime: string, contract_sections_id: number, comment: string, seconds_spend: number }} body
 * @returns {Promise<object>}
 */
export const addGeneralDesignReport = async (body) => {
  const response = await fetch(`${getAPIURL()}general_design/report`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const errorText = await response.text();
    let message = `Ошибка создания отчёта ОП: ${response.status} ${response.statusText}`;
    if (errorText) message += `. ${errorText}`;
    throw new Error(message);
  }
  return response.json();
};

/**
 * Получить отчёты эскизного проектирования по пользователю
 * GET /sketch_design/reports/get/by-user/{user_id}
 * @returns {Promise<Array<{ id: number, user_id: number, project_id: number, datetime: string, work_type_id: number, comment: string, seconds_spend: number, is_remote: boolean, user: object, project: { id, name, status }, work_type: object }>>}
 */
export const getSketchDesignReportsByUser = async (userId) => {
  const response = await fetch(
    `${getAPIURL()}sketch_design/reports/get/by-user/${userId}`,
    {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "include",
    }
  );
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Ошибка загрузки отчётов ЭП: ${response.status} ${response.statusText}. ${errorText}`
    );
  }
  return response.json();
};

/**
 * Обновить отчёт эскизного проектирования
 * PATCH /sketch_design/reports/update/{report_id}
 * Если не передавать параметры, они не изменятся.
 * @param {number} reportId
 * @param {{ project_id?: number, work_type_id?: number, datetime?: string, comment?: string, seconds_spend?: number, is_remote?: boolean }} body
 * @returns {Promise<{ id: number, user_id: number, project_id: number, datetime: string, work_type_id: number, comment: string, seconds_spend: number, is_remote: boolean, user: object, project: object, work_type: object }>}
 */
export const updateSketchDesignReport = async (reportId, body) => {
  const response = await fetch(
    `${getAPIURL()}sketch_design/reports/update/${reportId}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "include",
      body: JSON.stringify(body),
    }
  );
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Ошибка обновления отчёта ЭП: ${response.status} ${response.statusText}. ${errorText}`
    );
  }
  return response.json();
};

/**
 * Удалить отчёт эскизного проектирования
 * DELETE /sketch_design/reports/delete/{report_id}
 */
export const deleteSketchDesignReport = async (reportId) => {
  const response = await fetch(
    `${getAPIURL()}sketch_design/reports/delete/${reportId}`,
    {
      method: "DELETE",
      headers: { Accept: "application/json" },
      credentials: "include",
    }
  );
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Ошибка удаления отчёта ЭП: ${response.status} ${response.statusText}. ${errorText}`
    );
  }
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

/**
 * Получить отчёты Генплан РП по пользователю
 * GET /general_design/user_reports?user_id=[&month=&year=&contract_sections_id=]
 * month/year передавайте только если нужна фильтрация по периоду (например, в выплатах).
 * @returns {Promise<Array<{ id: number, contract_sections_id: number, seconds_spend: number, comment: string, datetime: string, user_id: number }>>}
 */
export const getGeneralDesignReportsByUser = async (userId, month, year) => {
  const extractReports = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.reports)) return data.reports;
    return [];
  };

  const hasPeriodFilter =
    Number.isInteger(month) &&
    month >= 1 &&
    month <= 12 &&
    Number.isInteger(year) &&
    year > 0;

  const fetchUserReports = async (withPeriod) => {
    const params = new URLSearchParams({ user_id: String(userId) });
    if (withPeriod && hasPeriodFilter) {
      params.set("month", String(month));
      params.set("year", String(year));
    }
    const response = await fetch(
      `${getAPIURL()}general_design/user_reports?${params}`,
      {
        method: "GET",
        headers: { Accept: "application/json" },
        credentials: "include",
      }
    );
    if (!response.ok) return [];
    const data = await response.json();
    return extractReports(data);
  };

  const reports = await fetchUserReports(hasPeriodFilter);
  if (reports.length > 0 || !hasPeriodFilter) {
    return reports;
  }

  // Запрос с month/year пустой — пробуем без периода (как в Swagger: только user_id)
  const allPeriodReports = await fetchUserReports(false);
  if (allPeriodReports.length > 0) {
    return allPeriodReports;
  }

  const contracts = await getGeneralDesignAllContracts(month, year);
  const contractList = Array.isArray(contracts) ? contracts : [];
  const sectionIds = [
    ...new Set(
      contractList
        .map((item) => item?.contract_sections_id)
        .filter((id) => id != null)
        .map((id) => Number(id))
        .filter((id) => !Number.isNaN(id))
    ),
  ];

  if (sectionIds.length === 0) {
    return [];
  }

  const bySection = await Promise.all(
    sectionIds.map((id) =>
      getGeneralDesignUserReports(month, year, id, userId)
        .then(extractReports)
        .catch(() => [])
    )
  );
  const seen = new Set();
  const merged = [];
  bySection.flat().forEach((report) => {
    if (report?.id == null || seen.has(report.id)) return;
    seen.add(report.id);
    merged.push(report);
  });
  return merged;
};

/**
 * Обновить отчёт Генплан РП
 * PATCH /general_design/report
 * @param {{ id: number, contract_sections_id: number, seconds_spend: number, comment: string }} body
 */
export const updateGeneralDesignReport = async (body) => {
  const response = await fetch(`${getAPIURL()}general_design/report`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const errorText = await response.text();
    let message = `Ошибка обновления отчёта ГП: ${response.status} ${response.statusText}`;
    if (response.status === 422) {
      try {
        const err = JSON.parse(errorText);
        const details = Array.isArray(err.detail)
          ? err.detail.map((d) => d.msg || JSON.stringify(d)).join("; ")
          : err.detail;
        if (details) message = `Ошибка валидации: ${details}`;
      } catch {
        if (errorText) message += `. ${errorText}`;
      }
    } else if (errorText) {
      message += `. ${errorText}`;
    }
    throw new Error(message);
  }
  return response.json();
};

/**
 * Удалить отчёт Генплан РП
 * DELETE /general_design/report?report_id=
 */
export const deleteGeneralDesignReport = async (reportId) => {
  const response = await fetch(
    `${getAPIURL()}general_design/report?report_id=${reportId}`,
    {
      method: "DELETE",
      headers: { Accept: "application/json" },
      credentials: "include",
    }
  );
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Ошибка удаления отчёта ГП: ${response.status} ${response.statusText}. ${errorText}`
    );
  }
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

export const sendReport = async (reportData) => {
  try {
    console.log("Детальный лог отправляемых данных:", {
      endpoint: `${getAPIURL()}reports/add`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      data: reportData,
    });

    const response = await fetch(`${getAPIURL()}reports/add`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(reportData),
    });

    console.log("Статус ответа:", response.status);
    console.log("Заголовки ответа:", response.headers);

    const responseText = await response.text();
    console.log("Текст ответа:", responseText);

    if (!response.ok) {
      console.error("Детали ошибки:", {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
        errorText: responseText,
      });

      throw new Error(
        `Ошибка сервера: ${response.status} ${response.statusText}. ${responseText}`
      );
    }

    const responseData = JSON.parse(responseText);
    console.log("Успешный ответ сервера:", responseData);
    return responseData;
  } catch (error) {
    console.error("Полная информация об ошибке:", {
      name: error.name,
      message: error.message,
      stack: error.stack,
    });
    throw error;
  }
};

export const getAllReports = async (
  userId,
  factDateStart,
  page = 1,
  pageSize = 1000
) => {
  try {
    const response = await fetch(
      `${getAPIURL()}reports/all?user_id=${userId}&fact_date_start=${factDateStart}&page=${page}&page_size=${pageSize}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      // Сначала проверим, является ли ответ JSON
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        const errorData = await response.json();
        throw new Error(
          `Ошибка при загрузке отчетов: ${JSON.stringify(errorData)}`
        );
      } else {
        // Если ответ не JSON, получаем текст ошибки
        const errorText = await response.text();
        throw new Error(`Ошибка сервера: ${response.status} ${errorText}`);
      }
    }

    const data = await response.json();
    return data.reports;
  } catch (error) {
    console.error("Ошибка при загрузке отчетов:", error);
    throw error;
  }
};

export const updateReport = async (reportData) => {
  try {
    console.log("Данные для отправки:", reportData);
    const response = await fetch(`${getAPIURL()}reports/${reportData.id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(reportData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error("Ошибка от сервера:", errorData);
      throw new Error(
        `Ошибка сети: ${response.status} ${
          response.statusText
        }\n${JSON.stringify(errorData)}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error("Ошибка при обновлении отчета:", error);
    throw error;
  }
};

export const deleteReport = async (reportId) => {
  try {
    console.log("Отправляем запрос на удаление отчета с ID:", reportId);
    const response = await fetch(`${getAPIURL()}reports/${reportId}/delete`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    });

    console.log("Статус ответа:", response.status);
    console.log("Заголовки ответа:", response.headers);

    const responseText = await response.text();
    console.log("Текст ответа:", responseText);

    if (!response.ok) {
      throw new Error(
        `Ошибка сервера: ${response.status} ${response.statusText}. ${responseText}`
      );
    }

    const responseData = JSON.parse(responseText);
    console.log("Ответ сервера после удаления отчета:", responseData);
    return responseData;
  } catch (error) {
    console.error("Ошибка при удалении отчета:", error);
    throw error;
  }
};

export const getOvertimeReports = async () => {
  try {
    const response = await fetch(`${getAPIURL()}reports/overtimes_review/all`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      if (response.status === 400) {
        return { overtimes: [] };
      }
      throw new Error(`Ошибка: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Ошибка при получении отчетов переработок:", error);
    return { overtimes: [] };
  }
};

export const updateOvertimeStatus = async (overtimeId, isApproved) => {
  try {
    const response = await fetch(`${getAPIURL()}reports/overtimes_review/add`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        overtime_id: overtimeId,
        is_approved: isApproved,
      }),
    });

    if (!response.ok) {
      throw new Error(`Ошибка: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Ошибка при обновлении статуса переработки:", error);
    throw error;
  }
};

export const getDayoffReports = async () => {
  try {
    const response = await fetch(`${getAPIURL()}days_off/review/all`, {
      method: "GET",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.days_off || [];
  } catch (error) {
    console.error("Ошибка при получении отгулов:", error);
    throw error;
  }
};

export const updateDayoffStatus = async (dayOffId, isApproved) => {
  try {
    const requestData = {
      day_off_id: dayOffId,
      is_approved: isApproved,
    };
    console.log("Отправляемые данные на сервер:", requestData);

    const response = await fetch(`${getAPIURL()}days_off/review/add`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Ответ сервера:", errorText);
      throw new Error(`Ошибка: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Ошибка при обновлении статуса отгула:", error);
    throw error;
  }
};

export const getUserOvertimes = async (userId, page = 1, pageSize = 20) => {
  try {
    const queryParams = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize),
    });
    const response = await fetch(
      `${getAPIURL()}reports/overtimes/${userId}?${queryParams.toString()}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Ошибка: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Ошибка при получении переработок пользователя:", error);
    throw error;
  }
};

/**
 * Экспорт переработок за период в XLSX
 * GET /overtime_exports/export
 * @param {string} dateFrom - Начало периода (YYYY-MM-DD)
 * @param {string} dateTo - Конец периода (YYYY-MM-DD)
 */
export const exportOvertimesExcel = async (dateFrom, dateTo) => {
  const params = new URLSearchParams({
    date_from: dateFrom,
    date_to: dateTo,
  });
  const url = `${getAPIURL()}overtime_exports/export?${params.toString()}`;

  const response = await fetch(url, {
    method: "GET",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    let errorMessage = "";
    try {
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        const errorData = await response.json();
        errorMessage =
          errorData.detail || errorData.message || errorData.error || "";
        if (Array.isArray(errorMessage)) {
          errorMessage = errorMessage
            .map((item) => item.msg || JSON.stringify(item))
            .join("; ");
        }
      } else {
        errorMessage = await response.text();
      }
    } catch (e) {
      errorMessage = "";
    }

    if (response.status === 504) {
      throw new Error(
        "Превышено время ожидания сервера. Попробуйте выбрать меньший период.",
      );
    }

    throw new Error(
      errorMessage
        ? `Ошибка при выгрузке переработок (${response.status}): ${errorMessage}`
        : `Ошибка при выгрузке переработок: ${response.status}`,
    );
  }

  const contentDisposition = response.headers.get("content-disposition");
  let filename = `Переработки_${dateFrom}_${dateTo}.xlsx`;
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(
      /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
    );
    if (filenameMatch && filenameMatch[1]) {
      filename = filenameMatch[1].replace(/['"]/g, "");
      try {
        filename = decodeURIComponent(filename);
      } catch (e) {}
    }
  }

  const blob = await response.blob();
  const downloadUrl = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = downloadUrl;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(downloadUrl);
};

/**
 * Переработки за период, сгруппированные по датам
 * GET /overtime_exports/timeline
 * @param {Object} params
 * @param {number} [params.year]
 * @param {number} [params.month]
 * @param {number} [params.day]
 * @param {string} [params.dateFrom] - YYYY-MM-DD
 * @param {string} [params.dateTo] - YYYY-MM-DD
 */
export const getOvertimeTimeline = async ({
  year,
  month,
  day,
  dateFrom,
  dateTo,
} = {}) => {
  const params = new URLSearchParams();

  if (dateFrom && dateTo) {
    params.append("date_from", dateFrom);
    params.append("date_to", dateTo);
  } else if (year != null && month != null && day != null) {
    params.append("year", String(year));
    params.append("month", String(month));
    params.append("day", String(day));
  } else if (year != null && month != null) {
    params.append("year", String(year));
    params.append("month", String(month));
  } else if (year != null) {
    params.append("year", String(year));
  } else {
    throw new Error("Не указан период для загрузки переработок");
  }

  const response = await fetch(
    `${getAPIURL()}overtime_exports/timeline?${params.toString()}`,
    {
      method: "GET",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    },
  );

  if (!response.ok) {
    let errorMessage = "";
    try {
      const errorData = await response.json();
      errorMessage = errorData.detail || errorData.message || "";
      if (Array.isArray(errorMessage)) {
        errorMessage = errorMessage
          .map((item) => item.msg || JSON.stringify(item))
          .join("; ");
      }
    } catch (e) {
      errorMessage = "";
    }

    throw new Error(
      errorMessage
        ? `Ошибка при загрузке переработок (${response.status}): ${errorMessage}`
        : `Ошибка при загрузке переработок: ${response.status}`,
    );
  }

  return response.json();
};

export const getLeaveTypes = async ({ group_id, user_id } = {}) => {
  try {
    const params = new URLSearchParams();
    if (group_id != null) params.set("group_id", group_id);
    if (user_id != null) params.set("user_id", user_id);
    const query = params.toString();
    const response = await fetch(
      `${getAPIURL()}portal_job_types/hr_inspector/all${query ? `?${query}` : ""}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        `Ошибка при загрузке типов отпусков: ${JSON.stringify(errorData)}`
      );
    }

    const data = await response.json();
    return data.map((type) => ({
      value: type.id,
      label: type.name,
    }));
  } catch (error) {
    console.error("Ошибка при загрузке типов отпусков:", error);
    throw error;
  }
};

export const addHrInspectorReport = async (reportData) => {
  try {
    console.log("Отправляемые данные на сервер:", {
      url: `${getAPIURL()}reports/hr_inspector/add`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      data: reportData,
    });

    const response = await fetch(`${getAPIURL()}reports/hr_inspector/add`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(reportData),
    });

    console.log("Статус ответа:", response.status);
    const responseText = await response.text();
    console.log("Текст ответа:", responseText);

    if (!response.ok) {
      throw new Error(
        `Ошибка при создании отчета: ${response.status}. ${responseText}`
      );
    }

    return responseText ? JSON.parse(responseText) : null;
  } catch (error) {
    console.error("Полная информация об ошибке:", {
      name: error.name,
      message: error.message,
      stack: error.stack,
      reportData,
    });
    throw error;
  }
};

/**
 * Создание компенсации (кадровые операции)
 * POST /reports/hr_inspector/add
 */
export const addHrInspectorCompensationReport = async (reportData) => {
  try {
    const response = await fetch(`${getAPIURL()}reports/hr_inspector/add`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(reportData),
    });

    const responseText = await response.text();

    if (!response.ok) {
      throw new Error(
        `Ошибка при создании компенсации: ${response.status}. ${responseText}`
      );
    }

    return responseText ? JSON.parse(responseText) : null;
  } catch (error) {
    console.error("Ошибка при создании компенсации:", error);
    throw error;
  }
};

export const addHrInspectorTravelReport = async (reportData) => {
  try {
    console.log("Отправляемые данные на сервер:", {
      url: `${getAPIURL()}reports/hr_inspector/add_travel_jobs`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      data: reportData,
    });

    const response = await fetch(
      `${getAPIURL()}reports/hr_inspector/add_travel_jobs`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(reportData),
      }
    );

    console.log("Статус ответа:", response.status);
    const responseText = await response.text();
    console.log("Текст ответа:", responseText);

    if (!response.ok) {
      throw new Error(
        `Ошибка при создании отчета о командировке: ${response.status}. ${responseText}`
      );
    }

    return responseText ? JSON.parse(responseText) : null;
  } catch (error) {
    console.error("Полная информация об ошибке:", {
      name: error.name,
      message: error.message,
      stack: error.stack,
      reportData,
    });
    throw error;
  }
};

export const getHrInspectorReports = async (params) => {
  const queryParams = new URLSearchParams({
    page: params.page || 1,
    page_size: params.page_size || 20,
    ...(params.group_id && { group_id: params.group_id }),
    ...(params.user_id && { user_id: params.user_id }),
  });

  const response = await fetch(
    `${getAPIURL()}reports/hr_inspector/reports?${queryParams}`,
    {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    throw new Error("Ошибка при получении отчетов");
  }

  return response.json();
};

export const deleteHrInspectorReport = async (reportId) => {
  try {
    const response = await fetch(
      `${getAPIURL()}reports/hr_inspector/${reportId}/delete`,
      {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Ошибка при удалении отчета: ${response.status}. ${errorText}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error("Ошибка при удалении отчета:", error);
    throw error;
  }
};

export const getUserOvertimeStats = async (userId) => {
  try {
    console.log("Fetching overtime stats for user:", userId);
    console.log("Current cookies:", document.cookie);

    const response = await fetch(
      `${getAPIURL()}reports/overtimes_time/${userId}`,
      {
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    console.log("Response status:", response.status);
    console.log(
      "Response headers:",
      Object.fromEntries(response.headers.entries())
    );

    if (!response.ok) {
      throw new Error("Ошибка при получении статистики переработок");
    }
    return await response.json();
  } catch (error) {
    console.error("Ошибка при получении статистики переработок:", error);
    throw error;
  }
};

export const getApprovedOvertimeReports = async () => {
  try {
    const response = await fetch(
      `${getAPIURL()}reports/overtimes/approved_by_me`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      if (response.status === 400) {
        return { overtimes: [] };
      }
      throw new Error(`Ошибка: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(
      "Ошибка при получении согласованных отчетов переработок:",
      error
    );
    return { overtimes: [] };
  }
};

/**
 * Проверка лимита отчётов за день (8 часов).
 * report_date — дата, за которую сдаётся отчёт (можно проверить любой день, не только сегодня).
 * При is_overtime=true ограничения не применяются.
 * @param {number} userId - ID пользователя
 * @param {number} [timeSpent=0] - Добавляемые секунды (для проверки превышения нормы)
 * @param {boolean} [isOvertime=false] - Является ли отчёт переработкой
 * @param {string} [reportDate] - Дата отчёта (YYYY-MM-DD). По умолчанию — сегодня
 * @returns {Promise<{ limitExceeded: boolean, message?: string }>}
 */
export const checkReportDayLimit = async (
  userId,
  timeSpent = 0,
  isOvertime = false,
  reportDate = undefined
) => {
  const params = new URLSearchParams({
    user_id: String(userId),
    time_spent: String(timeSpent),
    is_overtime: String(isOvertime),
  });
  if (reportDate) {
    params.set("report_date", reportDate);
  }
  const response = await fetch(
    `${getAPIURL()}reports/check-day-limit?${params}`,
    {
      method: "GET",
      credentials: "include",
      headers: { Accept: "application/json" },
    }
  );
  if (!response.ok) {
    if (response.status === 422) {
      const err = await response.json().catch(() => ({}));
      const details = err?.detail
        ? Array.isArray(err.detail)
          ? err.detail.map((d) => d.msg || JSON.stringify(d)).join("; ")
          : err.detail
        : response.statusText;
      throw new Error(`Ошибка проверки лимита: ${details}`);
    }
    throw new Error(
      `Ошибка проверки лимита: ${response.status} ${response.statusText}`
    );
  }
  const text = await response.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { raw: text };
  }
  const limitExceeded =
    data?.allowed === false ||
    data?.limit_exceeded === true ||
    data?.exceeded === true ||
    data?.limitExceeded === true ||
    (typeof data?.raw === "string" &&
      /exceed|превышен|лимит/i.test(data.raw));
  const timeSpentFromResponse =
    data?.time_spent !== undefined && data?.time_spent !== null
      ? Number(data.time_spent)
      : undefined;
  return {
    limitExceeded,
    message: data?.message || (limitExceeded ? "За этот день уже 8 часов отчёта" : undefined),
    time_spent:
      typeof timeSpentFromResponse === "number" && !Number.isNaN(timeSpentFromResponse)
        ? timeSpentFromResponse
        : undefined,
  };
};

export const getApprovedDayoffReports = async () => {
  try {
    const response = await fetch(
      `${getAPIURL()}days_off/review/approved_by_me`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Ошибка при получении согласованных отгулов:", error);
    return { days_off: [] };
  }
};
